import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
} from '@azure/msal-browser';
import { api, needsRenewal, session, setAuthProvider, setTokenRenewer } from './api.js';

/**
 * Signing in with Entra (FR-ID-01).
 *
 * WHICH TOKEN GOES WHERE, because there are two and sending the wrong one
 * fails in a way that looks like a broken login:
 *
 *   the id_token       goes to this API. Its `aud` is the application's own
 *                      client id, which is exactly what the server validates
 *                      — so no "Expose an API" scope has to be registered.
 *
 *   a Graph token      goes, separately, to the Graph MCP for directory
 *                      lookups. Different audience, different scopes, asked
 *                      for on its own. A token minted for this API is
 *                      refused by Graph and vice versa; they are not
 *                      interchangeable however similar they look.
 *
 * Redirect rather than popup: popups are blocked often enough that a login
 * which silently does nothing is a realistic outcome, and this is the first
 * thing anybody does.
 */

export interface AuthConfig {
  provider: 'dev' | 'entra';
  tenantId?: string;
  clientId?: string;
  audience?: string;
}

let client: PublicClientApplication | null = null;
let account: AccountInfo | null = null;

/**
 * Which provider is in force, learned once at boot.
 *
 * Held here so no component has to fetch it and none can disagree with
 * another about how people sign in. Defaults to 'entra' — the safer guess,
 * because the cost of wrongly hiding a dev shortcut is nothing and the cost
 * of wrongly showing one is an escalation affordance in a real deployment.
 */
let provider: AuthConfig['provider'] = 'entra';

export function isDevAuth(): boolean {
  return provider === 'dev';
}

export async function authConfig(): Promise<AuthConfig> {
  const cfg = await api.get<AuthConfig>('/api/v1/auth/config');
  provider = cfg.provider;
  setAuthProvider(cfg.provider);
  return cfg;
}

/**
 * The sign-in in progress, if there is one.
 *
 * A page load has exactly one sign-in. A second CALL must join the first,
 * never start another: two attempts race to redeem the same single-use
 * authorization code out of the URL, and Microsoft refuses the loser with
 * AADSTS54005 — which presents as a broken login on a perfectly good
 * credential.
 */
let attempt: Promise<void> | null = null;

/**
 * Bring up MSAL and make sure somebody is signed in.
 *
 * Returns once a token is on the session, or never returns because the page
 * is being redirected to the sign-in screen. Safe to call more than once:
 * every caller after the first awaits the same attempt.
 */
export function signIn(cfg: AuthConfig): Promise<void> {
  attempt ??= runSignIn(cfg).catch((err) => {
    // A failed attempt must not be cached, or a retry after a transient
    // failure would replay the failure for the life of the page.
    attempt = null;
    throw err;
  });
  return attempt;
}

async function runSignIn(cfg: AuthConfig): Promise<void> {
  if (cfg.provider !== 'entra' || !cfg.clientId || !cfg.tenantId) {
    throw new Error('Entra sign-in is not configured on the server.');
  }

  // ONE CLIENT PER PAGE LOAD. A second instance is a second opinion about a
  // login already in progress, and both of them will try to redeem the code
  // sitting in the URL.
  client ??= new PublicClientApplication({
    auth: {
      clientId: cfg.clientId,
      authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
      // Wherever the app is actually running. Registering the literal origin
      // keeps one registration working across localhost and a deployment,
      // provided both are listed as redirect URIs.
      redirectUri: window.location.origin,
    },
    cache: {
      // Survives a refresh, so a reload is not a re-login. Session storage
      // would sign somebody out every time they opened a ticket in a new tab.
      cacheLocation: 'localStorage',
    },
  });
  /*
   * NO `system` TIMEOUTS HERE, deliberately.
   *
   * The obvious fix for a slow silent renewal is to shorten MSAL's own frame
   * timeouts — `iframeHashTimeout` and friends. Those options were removed in
   * msal-browser v5 and setting them is a type error, so the bound lives in
   * `api.ts` instead, as a race around whatever renewer is registered. That
   * is the better place for it regardless: it also covers the dev provider,
   * and it does not have to be revisited when MSAL reshuffles its internals
   * again.
   */

  await client.initialize();

  /**
   * openid/profile/email mint an id_token carrying the claims the server
   * reads. Graph scopes are asked for separately, when needed, so a
   * directory permission the tenant has not granted cannot block signing in.
   */
  const scopes = ['openid', 'profile', 'email'];

  // COMING BACK FROM MICROSOFT. The result carries the id_token, so use it
  // rather than immediately asking for another one: a silent request with
  // nothing cached falls back to a hidden iframe, which browsers block by
  // default, and the symptom is a `timed_out` that looks like the network.
  const redirect = await client.handleRedirectPromise();
  if (redirect?.account) {
    account = redirect.account;
    client.setActiveAccount(account);
    session.token = redirect.idToken;
    installRenewer(scopes);
    return;
  }

  account = client.getActiveAccount() ?? client.getAllAccounts()[0] ?? null;
  if (!account) {
    await client.loginRedirect({ scopes });
    // loginRedirect navigates away; nothing after this runs.
    return;
  }

  // ALREADY SIGNED IN on a previous visit. A silent refresh belongs here —
  // and when it fails, because the session expired or the cookies are gone,
  // the answer is to sign in again rather than to report a timeout at
  // somebody who only opened the page.
  client.setActiveAccount(account);
  try {
    let res = await client.acquireTokenSilent({ account, scopes });
    // A CACHED ID TOKEN CAN BE EXPIRED. For sign-in scopes MSAL hands back
    // the id_token it has, fresh or not, and this page used to take it. The
    // first call then failed, the renewal failed, and the person was told
    // their session had ended — on every refresh. Ask for a real one.
    if (needsRenewal(res.idToken)) res = await client.acquireTokenSilent({ account, scopes, forceRefresh: true });
    if (needsRenewal(res.idToken)) throw new Error('no fresh token without signing in');
    session.token = res.idToken;
    installRenewer(scopes);
  } catch {
    await client.loginRedirect({ scopes });
  }
}

/**
 * The "Sign in" button after a session has ended: straight to Microsoft.
 *
 * NOT `signIn()` again. That returns the attempt already made on this page —
 * finished, with the token that has since gone — so the button used to
 * spin for ever, and a refresh brought back "your session has ended". A
 * silent sign-in has already failed by the time anyone presses this, so the
 * only thing worth doing is the interactive one.
 */
export async function signInAgain(cfg: AuthConfig): Promise<void> {
  attempt = null;
  session.token = null;
  if (!client) {
    // MSAL never came up on this page: a fresh start does the whole thing.
    await signIn(cfg);
    return;
  }
  await client.loginRedirect({
    scopes: ['openid', 'profile', 'email'],
    ...(account ? { loginHint: account.username } : {}),
  });
}

/**
 * Teach the API client how to get another hour.
 *
 * An id_token lasts about an hour. Without this the desk simply stops
 * working at the end of it — every call answers 401 and the only way out is
 * a reload, in the middle of whatever somebody was typing.
 *
 * `forceRefresh` matters. Left off, MSAL happily returns the same cached
 * id_token it already has, which is the one about to expire, and the renewal
 * quietly accomplishes nothing. Forcing it redeems the refresh token — over
 * fetch, not through the hidden iframe browsers block, which is what
 * registering the app as a Single-Page Application bought us.
 *
 * Interaction genuinely being required is not an error to report. Consent
 * withdrawn or an account removed both mean the same thing to the person
 * sitting there: they need to sign in. So send them, rather than telling
 * them to reload and hope.
 */
function installRenewer(scopes: string[]): void {
  setTokenRenewer(async () => {
    if (!client || !account) return null;
    try {
      const res = await client.acquireTokenSilent({ account, scopes, forceRefresh: true });
      session.token = res.idToken;
      return res.idToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await client.loginRedirect({ scopes });
        // Navigating away; nothing will read this.
        return null;
      }
      /**
       * A transient failure — offline, a blip at the authority, an iframe
       * the browser refused to load. Report it as "not renewed" rather than
       * throwing somebody out of a session that may still be fine.
       *
       * The caller decides what that means, and it now decides differently
       * depending on the token: still inside its life, the call is tried
       * anyway; already past `exp`, the session is declared over instead of
       * letting a dozen queries each rediscover it. `api.ts` also holds off
       * on further attempts for a cool-off, because this failure is usually
       * the kind that repeats.
       */
      return null;
    }
  });
}

/**
 * A token for Microsoft Graph, as the signed-in person.
 *
 * Separate from the API token and deliberately failure-tolerant: it backs a
 * decoration, and a tenant that has not consented to the directory scope
 * must not prevent anybody from using the desk. Null means "we do not know
 * who this person is", which the caller renders as no badge.
 */
export async function graphToken(): Promise<string | null> {
  if (!client || !account) return null;
  const scopes = ['https://graph.microsoft.com/User.Read'];
  try {
    const res = await client.acquireTokenSilent({ account, scopes });
    return res.accessToken;
  } catch (err) {
    // Consent has not been given for this scope, or it has expired. Asking
    // interactively here would throw a sign-in page at somebody who is
    // reading a ticket, so it is refused quietly instead.
    if (err instanceof InteractionRequiredAuthError) return null;
    return null;
  }
}

/**
 * Sign out of both halves.
 *
 * Our token AND MSAL's cached account. Clearing only ours leaves the account
 * cached, so the next load signs the same person back in without asking and
 * the button appears to do nothing — which is the worst outcome for a
 * control somebody reaches for on a shared machine.
 *
 * Under the dev provider there is no MSAL client, so this just drops the
 * token and goes back to the start.
 */
export function signOut(): void {
  session.token = null;
  session.tenant = null;
  // Whoever signs in next starts from their own roles, not this person's.
  session.staffDesks = null;
  // Or the next 401 renews the session of somebody who just left.
  setTokenRenewer(null);
  // Recorded BEFORE navigating: once logoutRedirect leaves the page, this
  // code does not run again, and without the flag the return trip looks
  // exactly like a first visit and signs straight back in.
  session.signedOut = 'user';

  if (client && account) {
    // Ends the session at Microsoft too, not just locally: a local-only
    // sign-out on a shared machine is barely a sign-out.
    void client.logoutRedirect({ account });
    return;
  }

  window.location.href = '/';
}
